import { Component, OnDestroy, OnInit } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { switchMap } from 'rxjs/operators';

// Angular Material & Editor
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { environment } from '../../../../environments/environment';
import { FuncionalidadeEnum } from '../../../shared/enums/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/modulo.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MensagemAdminService } from '../../../shared/services/apis/srv-notificacao/mensagem-admin.service';
import { MensagemRequest } from '../../../shared/services/requests/srv-notificacao/mensagem-request.request';
import { UsuarioAdminService } from '../../../shared/services/apis/usuario/usuario-admin.service';

@Component({
  selector: 'app-enviar-notificacao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MenuComponent,
    SelectPesquisavelComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    NgxEditorModule,
  ],
  templateUrl: './enviar-notificacao.component.html',
  styleUrl: './enviar-notificacao.component.css',
})
export class EnviarNotificacaoComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Suporte;
  public funcionalidadeEnum = FuncionalidadeEnum.Suporte_Notificacao;

  public model: FormGroup;
  public editor: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  public _isLoading: boolean = false;
  public _urlListEmpresa: string;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private alertService: AlertService,
    private mensagemService: MensagemAdminService,
    private usuarioAdminService: UsuarioAdminService
  ) {
    this._urlListEmpresa = environment.urlApiUsuario + 'ListaEmpresaAdmin';
  }

  ngOnInit(): void {
    this.editor = new Editor();
    this.model = this.fb.group({
      titulo: ['', [Validators.required, Validators.minLength(5)]],
      conteudo: ['', [Validators.required, Validators.minLength(10)]],
      enviarParaTodos: [false],
      empresas: [[], []],
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  get empresasControl(): FormControl {
    return this.model.get('empresas') as FormControl;
  }

  onChangeEnviarTodos(): void {
    const enviarTodos = this.model.get('enviarParaTodos')?.value;
    if (enviarTodos) {
      this.empresasControl.disable();
      this.empresasControl.setValue([]);
    } else {
      this.empresasControl.enable();
    }
  }

  cancelar(): void {
    this.router.navigate(['/suporte/notificacao']);
  }

  submit(): void {
    if (this.model.invalid) {
      this.model.markAllAsTouched();
      return;
    }

    const formVal = this.model.value;
    const listarTodos = formVal.enviarParaTodos;
    const listIdEmpresa = formVal.empresas || [];

    if (!listarTodos && listIdEmpresa.length === 0) {
      this.alertService.show(
        'Atenção',
        'Selecione pelo menos uma empresa ou marque "Enviar para todas".',
        AlertType.warning
      );
      return;
    }

    this._isLoading = true;

    // 1. Busca usuários já agrupados (otimizado no backend)
    this.usuarioAdminService
      .getAllByListIdEmpresa(listIdEmpresa, listarTodos)
      .pipe(
        switchMap(response => {
          // Se der erro ou lista vier vazia/nula
          if (response.isError) {
            throw new Error(response.errorDescription);
          }

          const listaAgrupada = response.listUsuarioEmpresaGroupStruct;

          if (!listaAgrupada || listaAgrupada.length === 0) {
            throw new Error('Nenhum usuário encontrado para os critérios selecionados.');
          }

          // 2. Monta o Request de envio (O objeto já está no formato certo!)
          const request: MensagemRequest = {
            tituloMensagem: formVal.titulo,
            conteudoMensagem: formVal.conteudo,
            listaUsuario: listaAgrupada,
          };

          // 3. Envia a notificação
          return this.mensagemService.post(request);
        })
      )
      .subscribe({
        next: response => {
          this._isLoading = false;
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
          } else {
            this.alertService.show('Sucesso', 'Notificação enviada com sucesso!', AlertType.success);
            this.router.navigate(['/suporte/notificacao']);
          }
        },
        error: err => {
          this._isLoading = false;
          // Se for erro tratado (throw new Error), vem em err.message
          // Se for erro HTTP, pode vir em err.error ou err.message
          const msg = err.message || 'Erro ao processar envio.';
          this.alertService.show('Erro', msg, AlertType.error);
        },
      });
  }
}
